import React, { useState, useEffect } from 'react';

import { MultiMetricOptionsProps, ModalFilter, ValueInputs } from '../../../types';
import MultiDropdown from '../../../components/MultiDropdown';

interface Props {
  marketFilters?: ModalFilter | null;
  retailerFilters?: ModalFilter | null;
  metricFilters?: ModalFilter | null;
  dropup: boolean;
  setSelectedMarkets: (value: React.SetStateAction<number[] | null>) => void;
  setSelectedMetrics: (value: React.SetStateAction<number[] | null>) => void;
  setSelectedRetailer: (value: React.SetStateAction<number[] | null>) => void;
  setCurrentPage?: (value: React.SetStateAction<number>) => void;
  tagsVisibility?: boolean;
  isEditing?: boolean;
  values?: ValueInputs;
  fetchingMetrics: boolean;
  gridWidth: number;
  isTopFilter?: boolean;
  setIsDropdownOpen?: (value: React.SetStateAction<boolean>) => void;
}

const NotesFilters = ({
  marketFilters,
  retailerFilters,
  metricFilters,
  setSelectedMarkets,
  setSelectedMetrics,
  setSelectedRetailer,
  dropup,
  setCurrentPage,
  tagsVisibility,
  isEditing,
  values,
  fetchingMetrics,
  gridWidth,
  isTopFilter,
  setIsDropdownOpen
}: Props): React.ReactElement => {
  const [marketOptions, setMarketOptions] = useState<MultiMetricOptionsProps[] | null>(null);
  const [metricOptions, setMetricOptions] = useState<MultiMetricOptionsProps[] | null>(null);
  const [retailerOptions, setRetailerOptions] = useState<MultiMetricOptionsProps[] | null>(null);

  useEffect(() => {
    if (marketFilters && !marketOptions) {
      const dataMetrics = [...marketFilters.values];
      const optionsFromData = dataMetrics.map((metric) => {
        return {
          id: metric.id,
          label: metric.name,
          visible: true
        };
      });
      setMarketOptions(optionsFromData);
    }
    if (retailerFilters && !retailerOptions) {
      const dataMetrics = [...retailerFilters.values];
      const optionsFromData = dataMetrics.map((metric) => {
        return {
          id: metric.id,
          label: metric.name,
          visible: true
        };
      });
      setRetailerOptions(optionsFromData);
    }
    if (metricFilters) {
      const dataMetrics = [...metricFilters.values];
      const optionsFromData = dataMetrics.map((metric) => {
        return {
          id: metric.id,
          label: metric.name,
          visible: true
        };
      });
      setMetricOptions(optionsFromData);
    }
  }, [marketFilters, metricFilters, retailerFilters]);

  const updatemarketFilters = (options: MultiMetricOptionsProps[]) => {
    if (marketOptions) {
      const newArr = [...marketOptions]; // copying the old datas array
      options.forEach((option, i) => {
        newArr[i].visible = option.visible;
      });
      const hasAllVisibility = options.every((option) => option.visible);
      const notHasAllVisibility = options.every((option) => !option.visible);
      setMarketOptions(newArr);
      setSelectedMarkets(
        notHasAllVisibility
          ? null
          : hasAllVisibility
          ? []
          : newArr.filter((val) => val.visible === true).map((val) => parseInt(val.id))
      );
      setCurrentPage && setCurrentPage(0);
    }
  };

  const updateMetricFilters = (options: MultiMetricOptionsProps[]) => {
    if (metricOptions) {
      const newArr = [...metricOptions]; // copying the old datas array
      options.forEach((option, i) => {
        newArr[i].visible = option.visible;
      });
      const hasAllVisibility = options.every((option) => option.visible);
      const notHasAllVisibility = options.every((option) => !option.visible);
      setMetricOptions(newArr);
      setSelectedMetrics(
        notHasAllVisibility
          ? null
          : hasAllVisibility
          ? []
          : newArr.filter((val) => val.visible === true).map((val) => parseInt(val.id))
      );
      setCurrentPage && setCurrentPage(0);
    }
  };

  const updateRetailerFilters = (options: MultiMetricOptionsProps[]) => {
    if (retailerOptions) {
      const newArr = [...retailerOptions]; // copying the old datas array
      options.forEach((option, i) => {
        newArr[i].visible = option.visible;
      });
      const hasAllVisibility = options.every((option) => option.visible);
      const notHasAllVisibility = options.every((option) => !option.visible);
      setRetailerOptions(newArr);
      setSelectedRetailer(
        notHasAllVisibility
          ? null
          : hasAllVisibility
          ? []
          : newArr.filter((val) => val.visible === true).map((val) => parseInt(val.id))
      );
      setCurrentPage && setCurrentPage(0);
    }
  };

  return (
    <div className={`bg-[#f3f3f3] flex items-center ${!isTopFilter ? 'flex-wrap' : ''} box-border p-2`}>
      {/* for metric set variant = metric */}
      <MultiDropdown
        label="Market"
        data={marketFilters ? marketFilters : null}
        onUpdate={(options) => updatemarketFilters(options)}
        scrollable={marketFilters ? (marketFilters.values.length > 4 ? true : false) : false}
        dropUp={dropup}
        noOptionMessage={'No markets selected'}
        tagsVisibility={tagsVisibility}
        values={values?.marketIds}
        isEditing={isEditing}
        disabled={marketFilters?.values.length === 1 ? true : false}
        gridWidth={gridWidth}
        isTopFilter={isTopFilter}
        setIsDropdownOpen={setIsDropdownOpen}
      />
      <MultiDropdown
        label="Retailer"
        data={retailerFilters ? retailerFilters : null}
        onUpdate={(options) => updateRetailerFilters(options)}
        scrollable={retailerFilters ? (retailerFilters.values.length > 4 ? true : false) : false}
        dropUp={dropup}
        noOptionMessage={'No retailers selected'}
        tagsVisibility={tagsVisibility}
        values={values?.retailerIds}
        isEditing={isEditing}
        disabled={retailerFilters?.values.length === 1 ? true : false}
        gridWidth={gridWidth}
        isTopFilter={isTopFilter}
        setIsDropdownOpen={setIsDropdownOpen}
      />
      <MultiDropdown
        label="Metric"
        data={metricFilters ? metricFilters : null}
        onUpdate={(options) => updateMetricFilters(options)}
        scrollable={metricFilters ? (metricFilters.values.length > 4 ? true : false) : false}
        dropUp={dropup}
        noOptionMessage={'No metrics selected'}
        tagsVisibility={tagsVisibility}
        isEditing={isEditing}
        values={values?.metricIds}
        disabled={metricFilters?.values.length === 1 ? true : fetchingMetrics}
        gridWidth={gridWidth}
        isTopFilter={isTopFilter}
        setIsDropdownOpen={setIsDropdownOpen}
      />
    </div>
  );
};

export default NotesFilters;
